import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

function CountTable({ title, data, testId }) {
    const entries = Object.entries(data || {});
    return (
        <div className="border border-slate-200 rounded-lg bg-white" data-testid={testId}>
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">{title}</div>
            <table className="w-full text-sm">
                <tbody>
                    {entries.length === 0 && <tr><td className="table-cell-tight text-slate-500">No data</td></tr>}
                    {entries.map(([k, v]) => (
                        <tr key={k} className="border-b border-slate-100" data-testid={`${testId}-row-${k}`}>
                            <td className="table-cell-tight font-medium text-slate-900 capitalize">{k.replace("_", " ")}</td>
                            <td className="table-cell-tight text-right text-slate-700">{v}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PatternList({ title, items, labelKey, testId, onCreateCase }) {
    return (
        <div className="border border-slate-200 rounded-lg bg-white" data-testid={testId}>
            <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">{title}</div>
            <table className="w-full text-sm">
                <tbody>
                    {(items || []).length === 0 && <tr><td colSpan={onCreateCase ? 3 : 2} className="table-cell-tight text-slate-500">No repeated patterns yet</td></tr>}
                    {(items || []).map((item, idx) => (
                        <tr key={`${item[labelKey]}-${idx}`} className="border-b border-slate-100" data-testid={`${testId}-row-${idx}`}>
                            <td className="table-cell-tight font-medium text-slate-900">{item[labelKey]}</td>
                            <td className="table-cell-tight text-right text-slate-700">{item.count}</td>
                            {onCreateCase && (
                                <td className="table-cell-tight text-right">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => onCreateCase(item)}
                                        data-testid={`${testId}-create-case-${idx}`}
                                    >
                                        Create case
                                    </Button>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AgencyDigest() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [digest, setDigest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionOpen, setActionOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [actionForm, setActionForm] = useState({
        action_type: "agency_chase",
        body: "",
        status: "",
        due_date: "",
        next_chase_date: "",
        response_source: "Agency digest",
        reference: "",
    });

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/agency/digest");
                setDigest(res.data);
            } catch (err) {
                toast.error("Failed to load digest");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const openAction = (item, actionType = "agency_chase") => {
        setSelectedItem(item);
        setActionForm({
            action_type: actionType,
            body: actionType === "agency_chase"
                ? `Follow-up from agency digest: ${item.action_needed}. Please confirm the current status, next action, expected completion date, and any blocker.`
                : "",
            status: "",
            due_date: "",
            next_chase_date: "",
            response_source: "Agency digest",
            reference: "",
        });
        setActionOpen(true);
    };

    const saveAction = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;
        try {
            await api.post(`/admin/triage/${selectedItem.id}/management-action`, {
                ...actionForm,
                visibility: "agency_shared",
                status: actionForm.status || null,
                due_date: actionForm.due_date || null,
                next_chase_date: actionForm.next_chase_date || null,
                response_source: actionForm.response_source || null,
                reference: actionForm.reference || null,
            });
            toast.success("Digest action recorded");
            setActionOpen(false);
            const res = await api.get("/agency/digest");
            setDigest(res.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not record digest action");
        }
    };

    const exportDigest = async () => {
        try {
            const res = await api.get("/agency/digest/export", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "agency-digest.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Digest CSV exported");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not export digest");
        }
    };

    const queueDigest = async () => {
        try {
            const res = await api.post("/admin/agency-digest/queue");
            toast.success(`Queued ${res.data?.queued || 0} digest delivery item(s)`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not queue digest delivery");
        }
    };

    const createPatternCase = async (patternType, item) => {
        const value = patternType === "location" ? item.location : item.contravention;
        try {
            const res = await api.post("/admin/agency-digest/pattern-case", {
                pattern_type: patternType,
                value,
                visibility: "agency_shared",
            });
            toast.success("Repeated PCN management case created");
            navigate(`/cases/${res.data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create repeated PCN case");
        }
    };

    const exportDigestPdf = async () => {
        try {
            const res = await api.get("/agency/digest/export.pdf", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "agency-digest.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Digest PDF exported");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not export digest PDF");
        }
    };

    return (
        <div className="space-y-6" data-testid="agency-digest-page">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Agency digest</div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Anonymized trends</h1>
                    <p className="text-sm text-slate-500 mt-1">GDPR-compliant. Counts only - no personal or property-specific identifiers.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" onClick={exportDigest} data-testid="export-agency-digest-button">
                        Export CSV
                    </Button>
                    <Button type="button" variant="outline" onClick={exportDigestPdf} data-testid="export-agency-digest-pdf-button">
                        Export PDF
                    </Button>
                    {user?.role === "estate_admin" && (
                        <Button type="button" className="bg-slate-700 hover:bg-slate-800 text-white" onClick={queueDigest} data-testid="queue-agency-digest-button">
                            Queue delivery
                        </Button>
                    )}
                </div>
            </div>

            {loading && <div className="text-sm text-slate-500">Loading…</div>}

            {!loading && digest && (
                <>
                    <div className="border border-slate-200 rounded-lg bg-white p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Total cases</div>
                        <div className="font-heading text-3xl font-bold text-slate-900 mt-1" data-testid="digest-total-cases">{digest.total_cases}</div>
                        <div className="text-xs text-slate-500 mt-1">Across the estate.</div>
                    </div>

                    <div className="border border-slate-200 rounded-lg bg-white p-5" data-testid="digest-management-summary">
                        <div className="text-xs uppercase tracking-widest text-slate-500">Management summary</div>
                        <p className="text-sm text-slate-800 mt-2">{digest.management_summary}</p>
                        <p className="text-xs text-slate-500 mt-2">{digest.privacy_note || digest.note}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="border border-slate-200 rounded-lg bg-white p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Stale open</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="digest-stale-cases">{digest.stale_open_cases || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-lg bg-white p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Safety open</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="digest-safety-cases">{digest.urgent_safety_cases || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-lg bg-white p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Agency shared</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="digest-agency-shared-cases">{digest.agency_shared_open_cases || 0}</div>
                        </div>
                        <div className="border border-slate-200 rounded-lg bg-white p-4">
                            <div className="text-xs uppercase tracking-widest text-slate-500">Reported PCNs</div>
                            <div className="font-heading text-2xl font-bold text-slate-900 mt-1" data-testid="digest-reported-pcns">{digest.reported_parking_cases || 0}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <CountTable title="By category" data={digest.by_category} testId="digest-by-category" />
                        <CountTable title="By status" data={digest.by_status} testId="digest-by-status" />
                        <PatternList
                            title="Repeated PCN locations"
                            items={digest.repeated_pcn_locations}
                            labelKey="location"
                            testId="digest-repeated-locations"
                            onCreateCase={user?.role === "estate_admin" ? (item) => createPatternCase("location", item) : null}
                        />
                        <PatternList
                            title="Repeated PCN contraventions"
                            items={digest.repeated_pcn_contraventions}
                            labelKey="contravention"
                            testId="digest-repeated-contraventions"
                            onCreateCase={user?.role === "estate_admin" ? (item) => createPatternCase("contravention", item) : null}
                        />
                    </div>

                    <section className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid="digest-agency-actions">
                        <div className="px-3 py-2 text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">Agency action items</div>
                        <table className="w-full text-sm">
                            {(digest.agency_action_items || []).length > 0 && (
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50">
                                        <th className="table-header-cell">Case</th>
                                        <th className="table-header-cell">Category</th>
                                        <th className="table-header-cell">Status</th>
                                        <th className="table-header-cell">Age</th>
                                        <th className="table-header-cell">Action</th>
                                        {user?.role === "estate_admin" && <th className="table-header-cell"></th>}
                                    </tr>
                                </thead>
                            )}
                            <tbody>
                                {(digest.agency_action_items || []).length === 0 && (
                                    <tr><td className="table-cell-tight text-slate-500">No agency-shared open cases.</td></tr>
                                )}
                                {(digest.agency_action_items || []).map((item) => (
                                    <tr key={item.id} className="border-b border-slate-100" data-testid={`digest-action-${item.id}`}>
                                        <td className="table-cell-tight font-medium text-slate-900">{item.title}</td>
                                        <td className="table-cell-tight text-slate-700 capitalize">{item.category}</td>
                                        <td className="table-cell-tight text-slate-700">{item.status?.replace("_", " ")}</td>
                                        <td className="table-cell-tight text-slate-700">{item.age_days}d</td>
                                        <td className="table-cell-tight text-slate-700">
                                            {item.action_needed}
                                            {item.latest_event_type && (
                                                <div className="text-xs text-slate-500 mt-1">
                                                    Last: {item.latest_event_type.replace("_", " ")}
                                                </div>
                                            )}
                                        </td>
                                        {user?.role === "estate_admin" && (
                                            <td className="table-cell-tight">
                                                <div className="flex items-center gap-2">
                                                    <Button type="button" variant="outline" size="sm" onClick={() => openAction(item, "agency_chase")} data-testid={`digest-chase-${item.id}`}>
                                                        Chase
                                                    </Button>
                                                    <Button type="button" variant="outline" size="sm" onClick={() => openAction(item, "agency_response")} data-testid={`digest-response-${item.id}`}>
                                                        Record response
                                                    </Button>
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <div className="text-xs text-slate-500" data-testid="digest-note">{digest.note}</div>

                    <Dialog open={actionOpen} onOpenChange={setActionOpen}>
                        <DialogContent data-testid="digest-action-dialog">
                            <DialogHeader>
                                <DialogTitle>Record digest action</DialogTitle>
                                <DialogDescription>
                                    Turn this digest item into a structured case action visible on the case timeline.
                                </DialogDescription>
                            </DialogHeader>
                            {selectedItem && (
                                <form onSubmit={saveAction} className="space-y-4">
                                    <div className="border border-slate-200 rounded-md p-3 text-sm text-slate-700">
                                        <div className="font-medium text-slate-900">{selectedItem.title}</div>
                                        <div className="text-xs text-slate-500 mt-1">{selectedItem.action_needed}</div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Action type</Label>
                                            <Select value={actionForm.action_type} onValueChange={(v) => setActionForm({ ...actionForm, action_type: v })}>
                                                <SelectTrigger data-testid="digest-action-type-select"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="agency_chase">Agency chase</SelectItem>
                                                    <SelectItem value="agency_response">Agency response</SelectItem>
                                                    <SelectItem value="escalation">Escalation</SelectItem>
                                                    <SelectItem value="management_response">Management response</SelectItem>
                                                    <SelectItem value="resolution">Resolution</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Set status</Label>
                                            <Select value={actionForm.status || "unchanged"} onValueChange={(v) => setActionForm({ ...actionForm, status: v === "unchanged" ? "" : v })}>
                                                <SelectTrigger data-testid="digest-action-status-select"><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="unchanged">Leave unchanged</SelectItem>
                                                    <SelectItem value="open">Open</SelectItem>
                                                    <SelectItem value="in_progress">In progress</SelectItem>
                                                    <SelectItem value="resolved">Resolved</SelectItem>
                                                    <SelectItem value="closed">Closed</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label>Action note</Label>
                                        <Textarea
                                            rows={4}
                                            value={actionForm.body}
                                            onChange={(e) => setActionForm({ ...actionForm, body: e.target.value })}
                                            required
                                            data-testid="digest-action-body-input"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Due date</Label>
                                            <Input type="date" value={actionForm.due_date || ""} onChange={(e) => setActionForm({ ...actionForm, due_date: e.target.value })} data-testid="digest-action-due-date-input" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Next chase</Label>
                                            <Input type="date" value={actionForm.next_chase_date || ""} onChange={(e) => setActionForm({ ...actionForm, next_chase_date: e.target.value })} data-testid="digest-action-next-chase-input" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label>Source</Label>
                                            <Input value={actionForm.response_source || ""} onChange={(e) => setActionForm({ ...actionForm, response_source: e.target.value })} data-testid="digest-action-source-input" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label>Reference</Label>
                                            <Input value={actionForm.reference || ""} onChange={(e) => setActionForm({ ...actionForm, reference: e.target.value })} placeholder="Email, PCN, portal, or contractor reference" data-testid="digest-action-reference-input" />
                                        </div>
                                    </div>
                                    <DialogFooter>
                                        <Button type="submit" className="bg-slate-700 hover:bg-slate-800 text-white" data-testid="digest-action-submit-button">
                                            Record action
                                        </Button>
                                    </DialogFooter>
                                </form>
                            )}
                        </DialogContent>
                    </Dialog>
                </>
            )}
        </div>
    );
}
